import {
  activeTab,
  stripIsWorthDrawing,
  tt,
  useAuth,
  useFranchiseModules,
  visibleTabs,
} from '@agos/shared'
import { EmptyState, PageHeader, TabGroup } from '@agos/ui'
import { Settings2 } from 'lucide-react'
import { useMemo, type ComponentType } from 'react'
import { useSearchParams } from 'wouter'

import { CustomerPaymentsSection } from '../customer-payments/CustomerPaymentsSection'
import { CourierSettingsSection } from '../delivery-settings/CourierSettingsSection'
import { DepotSection } from '../delivery-settings/DeliverySettingsPage'
import { QrPaymentSettingsBody } from '../qr-payment/QrPaymentSettingsPage'
import { DELIVERY_TABS } from './delivery-hub'

/**
 * `/delivery-settings` - FRONTEND.md §9.302.
 *
 * The owner asked for the QR payment settings to live here rather than under their own sidebar
 * entry. This is §9.208's shape, deliberately rather than a new one: a `TabGroup`, a map from route
 * to the body that route draws, and the tab list filtered by the routes' own guards.
 *
 * **`/qr-payment` still answers.** §9.208's rule - *"a hub cannot be a wall in front of a page
 * somebody has bookmarked"* - and `qr-payment.spec.ts` drives it by path. What changed is that the
 * sidebar now has one entry rather than two, which is what was asked for.
 *
 * **Bodies, not pages.** Each tab draws the content without its own `PageHeader`: the hub already
 * carries one, and the settings hub's habit of stacking two would have read *Nastavenia rozvozu*
 * twice on the first tab. The standalone routes still wrap their body in a header of its own.
 */

/* §9.766 - keyed by the tab's **id** rather than its path since *Platby zákazníkov*, which draws on the hub's own
   route and so shares the depot's path. */
/* §9.786, owner O139 - the external couriers' settings sit under the depot they are measured from. A separate card
   with its own save, drawn only for a franchise with the module. */
function DepotTab() {
  return (
    <div className="space-y-6">
      <DepotSection />
      <CourierSettingsSection />
    </div>
  )
}

const BODIES: Record<string, ComponentType> = {
  depot: DepotTab,
  qr: QrPaymentSettingsBody,
  'customer-payments': CustomerPaymentsSection,
}

export function DeliveryHubPage() {
  const { hasPermission, user } = useAuth()
  const { isModuleEnabled } = useFranchiseModules()
  const [params, setParams] = useSearchParams()

  const mine = useMemo(
    () =>
      visibleTabs(DELIVERY_TABS, {
        systemRole: user?.systemRole,
        hasPermission,
        isModuleEnabled,
      }),
    [user?.systemRole, hasPermission, isModuleEnabled],
  )

  const active = activeTab(mine, params.get('tab'))
  const current = mine.find((tab) => tab.id === active)
  const Body = current === undefined ? undefined : BODIES[current.id]

  return (
    <div className="space-y-5" data-delivery-hub={mine.length}>
      <PageHeader
        title={tt('Nastavenia rozvozu')}
        description={tt('Východiskový bod rozvozu, bankové údaje pre QR platbu a spôsoby platby zákazníkov.')}
        icon={<Settings2 className="w-5 h-5" />}
      />

      {/* One tab is not a choice, and a strip of one implies another this employee cannot have. */}
      {stripIsWorthDrawing(mine) && (
        <TabGroup
          variant="bar"
          tabs={mine.map((tab) => ({ id: tab.id, label: tab.label }))}
          active={active ?? ''}
          onChange={(id) => {
            // In the URL, so "open Nastavenia rozvozu → QR platba" is something somebody can send a
            // colleague, and so a reload does not forget which tab was open.
            const next = new URLSearchParams(params)
            next.set('tab', id)
            setParams(next, { replace: true })
          }}
        />
      )}

      {Body === undefined ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Settings2 className="w-10 h-10" />}
            title={tt('Žiadne nastavenia')}
            description={tt('Na žiadnu z týchto sekcií nemáte oprávnenie.')}
          />
        </div>
      ) : (
        <div data-delivery-tab={active}>
          <Body />
        </div>
      )}
    </div>
  )
}
